"use client";

import React from "react";
import { Mail, Phone, MapPin, Lock, ShieldCheck, Loader2, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-sm border border-input bg-muted/20 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-sm border border-input bg-muted/20 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="6181" id="request-supply-quote" ref={ref} className={`relative w-full py-24 overflow-hidden bg-background text-foreground transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-accent/10 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 text-primary rounded-sm flex items-center justify-center mb-2">
          <ShieldCheck className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground uppercase tracking-tight">Manifest Sent</h3>
        <p className="text-muted-foreground font-sans max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm font-mono text-xs tracking-widest">
          NEW REQUEST
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Enter Full Name" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="ops@logistics-hub.com" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase tracking-tighter opacity-70">Optional – for operations</FormDescription>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_contact" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Preferred Contact Method</FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-4">
                {["Email", "Phone", "No Preference"].map((option, i) => <label key={option} data-index={i} className={`
                      flex items-center gap-2 px-4 py-2 rounded-sm border cursor-pointer transition-all text-xs font-mono uppercase tracking-wider
                      ${field.value === option ? "border-primary bg-primary/10 text-primary ring-0" : "border-input bg-muted/10 hover:bg-muted/30 text-muted-foreground"}
                    `}>
                    <input type="radio" className="sr-only" checked={field.value === option} onChange={() => field.onChange(option)} value={option} />
                    <span className={`w-1.5 h-1.5 rounded-none ${field.value === option ? "bg-primary" : "bg-muted-foreground/30"}`} />
                    {option}
                  </label>)}
              </div>
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 20,
        message: "Message must be at least 20 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Logistics Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Specify cargo volume, origin, and destination details..." {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm flex items-center gap-2 text-destructive text-xs font-mono uppercase">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-sm font-mono tracking-widest rounded-sm shadow-lg shadow-primary/10 bg-primary text-primary-foreground hover:bg-primary/90 transition-all uppercase">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 h-4 w-4" />
            TRANSMITTING...
          </> : <>
            INITIATE QUOTE <Send className="ml-2 h-4 w-4" />
          </>}
      </Button>
      
      <p className="text-center text-[10px] font-mono text-muted-foreground mt-4 uppercase tracking-tighter">
        By submitting this manifest, you agree to our terms of service. Transmission is encrypted via 256-bit protocol.
      </p>
    </Form>;
}